import axios, { AxiosError, type AxiosInstance } from "axios";

import { paths } from "config/paths";
import type { ApiError } from "types";

import { buildProductionApiUrl } from "utils";
import { logout } from "./cookie_auth";

export const SUCCESS_STATUS_CODE = 200;
export const NO_CONTENT_STATUS_CODE = 204;
export const UNAUTHORIZED_STATUS_CODE = 401;
export const TOO_MANY_REQUESTS_STATUS_CODE = 429;
export const INTERNAL_SERVER_ERROR_STATUS_CODE = 500;

// If this is a local development environment -> use the Vite proxy.
const baseUrl = import.meta.env.DEV ? "/api/v2" : buildProductionApiUrl("/api/v2");

export const instance: AxiosInstance = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
  withXSRFToken: true,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  credentials: "same-origin",
  xsrfHeaderName: "X-CSRFToken",
  xsrfCookieName: "csrftoken",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json, text/plain, */*",
  },
});

instance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    const status = error.response?.status;
    const url = error.config?.url;
    if (status === UNAUTHORIZED_STATUS_CODE && url !== "/auth/login/") {
      logout();
      window.location.href = paths.login;
    }
    return Promise.reject(error);
  },
);

const getStatusCodeErrorMessage = (statusCode: number): string => {
  switch (statusCode) {
    case UNAUTHORIZED_STATUS_CODE:
      return "Invalid email or password. Please check your credentials and try again.";
    case TOO_MANY_REQUESTS_STATUS_CODE:
      return "Too many attempts. Please wait a moment and try again.";
    default:
      if (statusCode >= INTERNAL_SERVER_ERROR_STATUS_CODE) {
        return "Unexpected error. Please try again later.";
      }
      return "An unknown error occurred";
  }
};

const getApiErrorMessage = (data: ApiError): string => {
  if (data.detail && typeof data.detail === "string") {
    return data.detail;
  }
  return "An unknown error occurred";
};

export const getError = (error: unknown): string => {
  if (error instanceof AxiosError) {
    if (!error.response) {
      return "Please check your internet connection and try again.";
    }

    const statusCode = error.response.status;
    const statusMessage = getStatusCodeErrorMessage(statusCode);

    if (statusMessage !== "An unknown error occurred") {
      return statusMessage;
    }

    const data = error.response?.data as ApiError | undefined;
    if (!data) {
      return error.message || "An unknown error occurred.";
    }

    return getApiErrorMessage(data);
  }

  return error instanceof Error ? error.message : "An unknown error occurred.";
};

export default instance;
